import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { useEvent } from "../../../../context/EventContext";
import defaultProfilePic from "../../../../assest/defaultProfile.png";
import eventsAPI from "../../../../api/eventsAPI";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { storage } from "../../../../firebase";
import Loader from "../../../Loader/Loader";
import useFormFilledCalc from "../../../../hooks/useFormFilledCalc";
import { FaPlus, FaTrash } from "react-icons/fa";

// The ProfileForm component renders a multi-step form for updating a user's profile data.
// The form data is sent to the backend via a POST request when the user clicks the "Save" button.
// The component also handles image uploads and stores the image in Firebase Storage.
// The component also displays a progress bar while the image is uploading.
// The component also displays a success message when the form is submitted successfully.
// The component also displays an error message when the form is not submitted successfully.
// The component also updates the user's data in the AuthContext when the form is submitted successfully.
// The component also calls the setIsFormVisible function passed as a prop to hide the form when form is submitted successfully.
const ProfileForm = ({ setIsFormVisible, isUpdating }) => {
  const { user, setUser } = useAuth();
  const {
    setLoading,
    loading,
    setProfileFormFilledPercentage,
    handleDeleteImage,
  } = useEvent();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [profile, setProfile] = useState(user);
  const [selectedFile, setSelectedFile] = useState(null);
  const [socialLinkVisible, setSocialLinkVisible] = useState(false);
  const [addIcon, setAddIcon] = useState(true);
  const [socialPlatform, setSocialPlatform] = useState("");
  const [socialLink, setSocialLink] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedProfile = { ...profile, [name]: value };
    setProfile(updatedProfile);
  };
  //   Handles the file selection event by setting the selectedFile state variable to the chosen file.
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const { calculateProfileFormFilledPercentage } = useFormFilledCalc();

  // Run the percentage calculation whenever the profile form data changes
  useEffect(() => {
    const percentage = calculateProfileFormFilledPercentage(
      selectedFile,
      profile
    );
    setProfileFormFilledPercentage(percentage);
    // eslint-disable-next-line
  }, [profile, selectedFile]);

  const addSocialLink = () => {
    setSocialLinkVisible(true);
    setAddIcon(false);
  };

  const handleSocialSave = () => {
    const updatedProfile = {
      ...profile,
      socialLink: {
        ...profile.socialLink,
        [socialPlatform]: socialLink,
      },
    };
    setProfile(updatedProfile);
    setSocialLinkVisible(false);
    setAddIcon(true);
  };

  // Delete a specific social platform link
  const handleSocialDelete = (platform) => {
    const updatedLinks = { ...profile.socialLink };
    delete updatedLinks[platform]; // Remove the selected platform
    setProfile((prevProfile) => ({
      ...prevProfile,
      socialLink: updatedLinks,
    }));
  };

  //   Handles the change event of any input field in the profile form by
  //   updating the profile state with the new value.

  //   Handles the "Save" button click in the profile form.
  //   If an image is selected, it uploads the image to Firebase Storage and then sends the image URL in the request.
  //   If the request is successful, it shows a success message and updates the user object with the new profile data.
  //   If the request fails, it shows an error message.
  //   If no image is selected, it sends the profile form data to the server without an image.
  //   If the request is successful, it shows a success message and updates the user object with the new profile data.
  //   If the request fails, it shows an error message.
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedFile !== null) {
        // upload image and get download url
        const imageRef = storage.ref(`ProfilePics/${user.id}`);
        // Reference for image storage
        const uploadTask = imageRef.put(selectedFile); // Upload the image
        // Handle upload progress (optional)
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            // Progress logic (e.g., show a progress bar)
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress.toFixed(2));
          },
          (error) => {
            console.error("Error uploading image:", error);
            setLoading(false); // Stop loading
          },
          async () => {
            // Wait for upload to complete
            const imageUrl = await imageRef.getDownloadURL();
            // Call the API with updated formData
            const response = await eventsAPI.post("/profile", {
              ...profile,
              profilePicture: imageUrl,
            });
            if (response.status === 200) {
              toast.success("Profile updated successfully!");
              // Update the formData with the image URL
              setUser({ ...profile, profilePicture: imageUrl });
              setIsFormVisible(false);
            }
            setLoading(false);
          }
        );
      } else {
        const response = await eventsAPI.post("/profile", profile);
        if (response.status === 200) {
          setUser(profile);
          setIsFormVisible(false);
        } else {
          console.log("Error updating profile:", response.data);
          setLoading(false);
        }
        setLoading(false);
      }
    } catch (error) {
      console.error("Error updating event:", error);
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="form-section">
        <div className="form-group">
          <label>FIRST NAME:</label>
          <input
            name="firstName"
            type="text"
            placeholder="First Name"
            value={profile.firstName}
            onChange={handleChange}
          />
          <label>LAST NAME:</label>
          <input
            name="lastName"
            type="text"
            placeholder="Last Name"
            value={profile.lastName}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>BIO:</label>
          <textarea
            name="bio"
            placeholder="Describe about you"
            rows={5}
            cols={50}
            value={user.bio}
            onChange={handleChange}
          ></textarea>
        </div>
        <div className="form-group">
          <label>AGE:</label>
          <input
            name="age"
            type="number"
            placeholder="Enter your age"
            value={profile.age}
            onChange={handleChange}
          />
          <label>GENDER:</label>
          <select name="gender" value={profile.gender} onChange={handleChange}>
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div className="form-group">
          <label>CURRENT JOB STATUS:</label>
          <select
            name="jobStatus"
            value={profile?.jobStatus}
            onChange={handleChange}
          >
            <option value="">Select Job Status</option>
            <option value="student">Student</option>
            <option value="employed">Employed</option>
            <option value="self-employed">Self-Employed</option>
            <option value="unemployed">Unemployed</option>
          </select>
        </div>

        {profile.jobStatus === "student" && (
          <div className="form-group">
            <label>University Name:</label>
            <input
              type="text"
              placeholder="Enter your university name"
              name="universityName"
              value={profile?.universityName}
              onChange={handleChange}
            />
            <label>Degree Name:</label>
            <input
              type="text"
              placeholder="Enter your degree"
              name="degreeName"
              value={profile?.degreeName}
              onChange={handleChange}
            />
            <label>Graduation Year:</label>
            <input
              type="number"
              placeholder="Enter your year"
              name="GraduationYear"
              value={profile?.GraduationYear}
              onChange={handleChange}
            />
          </div>
        )}

        {profile.jobStatus === "employed" && (
          <div className="form-group">
            <label>COMPANY NAME:</label>
            <input
              type="text"
              placeholder="Enter your company name"
              name="companyName"
              value={profile.companyName}
              onChange={handleChange}
            />
            <label>Role:</label>
            <input
              type="text"
              placeholder="Enter your role"
              name="role"
              value={profile.role}
              onChange={handleChange}
            />
            <label>Description:</label>
            <textarea
              name="desc"
              value={profile.desc}
              onChange={handleChange}
              placeholder="Describe about you"
              rows={5}
              cols={50}
            ></textarea>
          </div>
        )}

        {profile.jobStatus === "self-employed" && (
          <div className="form-group">
            <label>Company Name:</label>
            <input
              type="text"
              placeholder="Enter your company name"
              name="compName"
              value={profile.companyName}
              onChange={handleChange}
            />
            <label>Role:</label>
            <input
              type="text"
              placeholder="Enter your role"
              name="Role"
              value={profile.Role}
              onChange={handleChange}
            />
            <label>Description:</label>
            <textarea
              name="description"
              value={profile.description}
              onChange={handleChange}
              placeholder="Describe about you"
              rows={5}
              cols={50}
            ></textarea>
          </div>
        )}

        {profile.jobStatus === "unemployed" && (
          <div className="form-group">
            <label>Preference Role:</label>
            <input
              type="text"
              placeholder="Enter your preference role"
              name="prefRole"
              value={profile.prefRole}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="upload-container">
          <label htmlFor="profilePicture">Choose Avatar</label>
          {selectedFile ? (
            <img
              src={URL.createObjectURL(selectedFile)}
              alt="Profile Pic"
              style={{ height: "60px", width: "60px", objectFit: "cover" }}
            />
          ) : user.profilePicture ? (
            <img
              src={user.profilePicture}
              alt="Profile Pic"
              style={{ height: "60px", width: "60px", objectFit: "cover" }}
            />
          ) : (
            <img
              src={defaultProfilePic}
              alt="Profile Pic"
              style={{ height: "60px", width: "60px", objectFit: "cover" }}
            />
          )}
          <input
            type="file"
            name="profilePicture"
            onChange={handleFileChange}
          />
          <button
            type="button"
            className="add-space"
            onClick={handleDeleteImage}
          >
            Delete
          </button>
          {uploadProgress > 0 && (
            <div className="progress-bar">
              <div
                className="progress"
                style={{
                  width: `${uploadProgress}%`,
                  backgroundColor: "green",
                  color: "white",
                }}
              >
                {uploadProgress}%
              </div>
            </div>
          )}
          <p className="upload-info">Valid file formats: JPG, GIF, PNG.</p>
        </div>

        <div className="form-group">
          <label>SOCIAL LINK</label>
          <div className="form-group">
            {/* Dropdown for selecting social platform */}
            {addIcon && <FaPlus onClick={addSocialLink} />}
            {socialLinkVisible ? (
              <>
                <label>Select Platform</label>
                <select
                  name="platform"
                  onChange={(e) => setSocialPlatform(e.target.value)}
                >
                  <option value="">Select Platform</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="other">Other</option>
                </select>
                {socialPlatform === "other" && (
                  <input
                    name="otherPlatform"
                    type="text"
                    placeholder="Enter other platform"
                    onChange={(e) => setSocialPlatform(e.target.value)}
                  />
                )}
                <input
                  name="link"
                  type="text"
                  placeholder="Paste link here"
                  onChange={(e) => setSocialLink(e.target.value)}
                  //   value={profile.link}
                />
                <button className="add-space" onClick={handleSocialSave}>
                  Save
                </button>
              </>
            ) : (
              <div>
                {profile.socialLink &&
                  Object.keys(profile.socialLink).map((platform) => (
                    <p key={platform}>
                      {platform}: {profile.socialLink[platform]}
                      <FaTrash
                        onClick={() => handleSocialDelete(platform)}
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                      />
                    </p>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <button className="create-button" onClick={handleSave}>
            {loading ? <Loader /> : isUpdating ? "Update" : "Save"}
          </button>
          <button
            className="create-button"
            onClick={() => setIsFormVisible(false)}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileForm;
